import { Controller } from "@hotwired/stimulus"
import { logger } from "../utilities/logger"

// Tom Select autocomplete controller
// Replaces jQuery UI Autocomplete with modern, zero-dependency autocomplete
//
// Usage:
//   select data-controller="autocomplete"
//          data-autocomplete-url-value="/api/search"
//          data-autocomplete-min-length-value="2"
//
// For simple local data:
//   select data-controller="autocomplete"
//
// For AJAX remote data:
//   select data-controller="autocomplete"
//          data-autocomplete-url-value="/program_operators/search"
//          data-autocomplete-query-param-value="query"
//
// Options:
//   url: AJAX endpoint for remote data
//   queryParam: Query parameter name (default: "query")
//   minLength: Minimum characters before searching (default: 1)
//   maxOptions: Maximum options to display (default: 50)
//   create: Allow creating new options (default: false)
//   multiple: Allow multiple selections (default: false)
//   placeholder: Placeholder text
//   valueField: Field to use as value (default: "value" or "id")
//   labelField: Field to use as label (default: "label" or "name")
//   searchField: Fields to search in (default: labelField)
//
export default class extends Controller {
  static values = {
    url: String,
    queryParam: { type: String, default: "query" },
    minLength: { type: Number, default: 1 },
    maxOptions: { type: Number, default: 50 },
    create: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    placeholder: String,
    valueField: { type: String, default: "value" },
    labelField: { type: String, default: "label" },
    searchField: String,
    additionalTarget: String,
    additionalAttribute: String
  }

  connect() {
    // Tom Select is loaded globally via CDN in application.slim
    if (typeof TomSelect === 'undefined') {
      logger.error('Tom Select library not loaded')
      return
    }

    // Check if the element is disabled (read-only mode)
    this.isReadOnly = this.element.disabled

    this.initializeSelect()

    // If read-only, disable Tom Select immediately after initialization
    if (this.isReadOnly && this.select) {
      this.select.disable()
    }
  }

  disconnect() {
    if (this.select) {
      this.select.destroy()
    }
  }

  initializeSelect() {
    // Enable multiple selection mode (tags) BEFORE initializing
    if (this.multipleValue) {
      this.element.setAttribute('multiple', 'multiple')
    }

    // Get translations from window.I18n (set by javascript_translations helper)
    const i18n = window.I18n?.tom_select || {}

    const config = {
      create: this.createValue,
      maxOptions: this.maxOptionsValue,
      placeholder: this.hasPlaceholderValue ? this.placeholderValue : (i18n.placeholder || null),
      valueField: this.valueFieldValue,
      labelField: this.labelFieldValue,
      searchField: this.hasSearchFieldValue ? this.searchFieldValue.split(',') : [this.labelFieldValue],
      // Only sort for remote data (URL-based); preserve original order for local options
      // This ensures user-defined option order is maintained in select fields
      sortField: this.hasUrlValue ? {
        field: this.labelFieldValue,
        direction: 'asc'
      } : null,
      // RTL support
      direction: document.dir === 'rtl' ? 'rtl' : 'ltr',
      // Plugin configuration for tags mode
      plugins: this.multipleValue ? ['remove_button'] : [],
      // Localized messages
      render: {
        no_results: () => `<div class="no-results">${i18n.no_results || 'No results found'}</div>`,
        option_create: (data, escape) => {
          // Replace {input} placeholder with actual input value
          const template = i18n.create || 'Add: {input}'
          return `<div class="create">${template.replace('{input}', escape(data.input))}</div>`
        },
        loading: () => `<div class="loading">${i18n.loading || 'Loading...'}</div>`
      },
      // Don't clear on blur if we're allowing creation
      onBlur: () => {
        if (!this.createValue) {
          this.select.setTextboxValue('')
        }
      }
    }

    // Configure AJAX remote data loading
    if (this.hasUrlValue) {
      // Throttle AJAX requests
      config.loadThrottle = 300

      // Preload options when dropdown opens (fixes tag replacement issue)
      config.preload = 'focus'

      config.load = (query, callback) => {
        // Allow empty query for preload
        if (!query && config.preload) {
          query = ''
        }

        // Don't search if query is too short (except for preload)
        if (query.length < this.minLengthValue && query !== '') {
          callback()
          return
        }

        const url = new URL(this.urlValue, window.location.origin)
        url.searchParams.set(this.queryParamValue, query)

        fetch(url)
          .then(response => response.json())
          .then(data => {
            // Handle additional target field (for programs_autocomplete pattern)
            if (this.hasAdditionalTargetValue && Array.isArray(data)) {
              // Transform data to include additional attribute
              const transformed = data.map(item => ({
                [this.valueFieldValue]: item.code || item.value || item.id,
                [this.labelFieldValue]: item.label || `${item.code}: ${item.name}`,
                [this.additionalAttributeValue]: item[this.additionalAttributeValue]
              }))
              callback(transformed)
            } else {
              callback(data)
            }
          })
          .catch(() => {
            callback()
          })
      }
    }

    // Initialize Tom Select
    this.select = new TomSelect(this.element, config)

    // Cmd/Ctrl+C with active tag(s) → copy their labels to the clipboard.
    // TomSelect's "active" highlight is a CSS class, not a native text
    // selection, so the browser's default copy is a no-op without this.
    this.select.wrapper.addEventListener('keydown', (e) => {
      if (e.key !== 'c' || !(e.ctrlKey || e.metaKey)) return
      const items = this.select.activeItems
      if (!items.length) return
      const labels = items.map(el => {
        const value = el.dataset.value
        return this.select.options[value]?.[this.labelFieldValue] ?? el.textContent.trim()
      })
      navigator.clipboard.writeText(labels.join(', '))
      e.preventDefault()
      e.stopPropagation()
    })

    // Apply is-invalid class to TomSelect wrapper if original select has it
    // Remove TomSelect's default 'invalid' class and use Bootstrap's 'is-invalid' instead
    if (this.element.classList.contains('is-invalid')) {
      this.select.wrapper.classList.remove('invalid')
      this.select.wrapper.classList.add('is-invalid')
    } else {
      // Also remove invalid class if it exists but is-invalid is not present
      this.select.wrapper.classList.remove('invalid')
    }

    // Handle additional target field updates (for programs_autocomplete pattern)
    if (this.hasAdditionalTargetValue) {
      this.select.on('change', (value) => {
        const item = this.select.options[value]
        if (item && this.hasAdditionalAttributeValue) {
          const targetElement = document.getElementById(this.additionalTargetValue)
          if (targetElement) {
            targetElement.value = item[this.additionalAttributeValue] || ''
          }
        }
      })
    }
  }

  // Allow programmatic value setting
  setValue(value) {
    if (this.select) {
      this.select.setValue(value)
    }
  }

  // Clear the selection
  clear() {
    if (this.select) {
      this.select.clear()
    }
  }

  // Add an option
  addOption(data) {
    if (this.select) {
      this.select.addOption(data)
    }
  }
}
